<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { authService } from '../services/auth';
import { collaborationApi, type Permission } from '../services/collaboration';

interface RemoteCase { id: string; ownerId: string; version: number; updatedAt: number; permission: Permission; }
interface Member { userId: string; email: string; name: string; permission: Permission; createdAt: number; }
interface Invitation { id: string; caseId: string; email: string; permission: 'read'|'edit'; createdAt: number; }

const router = useRouter();
const authenticated = ref(false);
const user = ref<any>(null);
const cases = ref<RemoteCase[]>([]);
const selectedCaseId = ref('');
const members = ref<Member[]>([]);
const incoming = ref<Invitation[]>([]);
const outgoing = ref<Invitation[]>([]);
const email = ref('');
const permission = ref<'read'|'edit'>('read');
const busy = ref(false);
const error = ref('');
const message = ref('');
const invitationLink = ref('');
const showTransfer = ref(false);
const transferTarget = ref('');
const requestedCaseId = ref('');

const selectedCase = computed(() => cases.value.find(c => c.id === selectedCaseId.value));
const isOwner = computed(() => selectedCase.value?.permission === 'owner');
const transferCandidates = computed(() => members.value.filter(m => m.permission !== 'owner'));

function currentUser() { const state = authService.state(); authenticated.value = !!state.authenticated; user.value = state.user; }
function clearFeedback() { error.value = ''; message.value = ''; }
function caseLabel(c: RemoteCase) { return c.id.length > 44 ? `${c.id.slice(0, 41)}…` : c.id; }
function permissionLabel(p: Permission|'owner') { return p === 'owner' ? 'Propietario' : p === 'edit' ? 'Edición' : 'Lectura'; }
function formatTime(value:number) { return new Date(value).toLocaleString(); }

async function syncCurrentLocalCase(caseId:string) {
  if (!caseId) return false;
  try {
    const frame = document.querySelector<HTMLIFrameElement>('.legacy-workspace__frame');
    const bridge = (frame?.contentWindow as any)?.__mindplaceSyncStore;
    if (bridge?.syncCurrentCase) {
      const result = await bridge.syncCurrentCase();
      if (result?.ok) { message.value = 'Caso local sincronizado con el servidor.'; return true; }
      if (result?.error) message.value = result.error;
    }
  } catch (e:any) { message.value = e?.message || 'No se pudo sincronizar el caso local.'; }
  return false;
}

async function loadAll() {
  clearFeedback();
  try {
    requestedCaseId.value = String(router.currentRoute.value.query.caseId || '');
    let result = await Promise.all([collaborationApi.listCases(), collaborationApi.invitations()]);
    cases.value = result[0] as RemoteCase[];
    incoming.value = (result[1].invitations || []) as Invitation[];
    if (requestedCaseId.value && !cases.value.some(c => c.id === requestedCaseId.value)) {
      await syncCurrentLocalCase(requestedCaseId.value);
      cases.value = await collaborationApi.listCases();
    }
    if (requestedCaseId.value && cases.value.some(c => c.id === requestedCaseId.value)) selectedCaseId.value = requestedCaseId.value;
    else if (!selectedCaseId.value || !cases.value.some(c => c.id === selectedCaseId.value)) selectedCaseId.value = cases.value[0]?.id || '';
    if (selectedCaseId.value) await loadCase(selectedCaseId.value);
    else if (requestedCaseId.value) {
      error.value = authenticated.value
        ? 'El caso abierto aún no está sincronizado con el servidor. Vuelve al tablero y pulsa Compartir de nuevo.'
        : 'El caso abierto no está disponible en el servidor. Inicia sesión y vuelve a intentarlo.';
    }
  } catch (e:any) { error.value = e?.message || 'No se pudieron cargar los datos de compartición.'; }
}

async function loadCase(id:string) {
  selectedCaseId.value = id; clearFeedback(); invitationLink.value = ''; showTransfer.value = false; transferTarget.value = '';
  try {
    const [m, o] = await Promise.all([collaborationApi.members(id), isOwner.value ? collaborationApi.outgoingInvitations(id) : Promise.resolve({ invitations: [] })]);
    members.value = m.members || [];
    outgoing.value = o.invitations || [];
  } catch (e:any) { error.value = e?.message || 'No se pudo cargar la colaboración del caso.'; }
}

async function share() {
  if (!selectedCaseId.value || !email.value.trim()) return;
  busy.value = true; clearFeedback();
  try {
    const result = await collaborationApi.share(selectedCaseId.value, email.value.trim(), permission.value);
    if (result.invited) {
      invitationLink.value = `${window.location.origin}/registro?invitacion=${encodeURIComponent(result.invitation.id)}`;
      message.value = 'Invitación creada. Comparte el enlace con el destinatario.';
    } else {
      message.value = 'Acceso concedido correctamente.';
    }
    email.value = '';
    await loadAll();
  } catch (e:any) { error.value = e?.message || 'No se pudo compartir el caso.'; }
  finally { busy.value = false; }
}

async function changePermission(member:Member) {
  busy.value = true; clearFeedback();
  try { await collaborationApi.permission(selectedCaseId.value, member.userId, member.permission === 'edit' ? 'read' : 'edit'); await loadCase(selectedCaseId.value); message.value = 'Permiso actualizado.'; }
  catch (e:any) { error.value = e?.message || 'No se pudo cambiar el permiso.'; }
  finally { busy.value = false; }
}

async function remove(member:Member) {
  if (!window.confirm(`¿Quitar el acceso de ${member.name || member.email}?`)) return;
  busy.value = true; clearFeedback();
  try { await collaborationApi.remove(selectedCaseId.value, member.userId); await loadCase(selectedCaseId.value); message.value = 'Acceso revocado.'; }
  catch (e:any) { error.value = e?.message || 'No se pudo revocar el acceso.'; }
  finally { busy.value = false; }
}

async function revokeInvitation(inv:Invitation) {
  if (!window.confirm(`¿Cancelar la invitación para ${inv.email}?`)) return;
  busy.value = true; clearFeedback();
  try { await collaborationApi.revokeInvitation(selectedCaseId.value, inv.id); await loadCase(selectedCaseId.value); message.value = 'Invitación cancelada.'; }
  catch (e:any) { error.value = e?.message || 'No se pudo cancelar la invitación.'; }
  finally { busy.value = false; }
}

async function accept(inv:Invitation) {
  busy.value = true; clearFeedback();
  try { await collaborationApi.acceptInvitation(inv.id); message.value = 'Invitación aceptada.'; await loadAll(); }
  catch (e:any) { error.value = e?.message || 'No se pudo aceptar la invitación.'; }
  finally { busy.value = false; }
}

async function transferOwnership() {
  if (!transferTarget.value || !selectedCaseId.value) return;
  const target = members.value.find(m => m.userId === transferTarget.value);
  if (!target) return;
  if (!window.confirm(`Transferir la propiedad de “${caseLabel(selectedCase.value!)}” a ${target.name || target.email}? Tú pasarás a tener permiso de edición.`)) return;
  busy.value = true; clearFeedback();
  try {
    await collaborationApi.transferOwnership(selectedCaseId.value, target.userId);
    showTransfer.value = false; transferTarget.value = '';
    message.value = 'Propiedad transferida correctamente.';
    await loadAll();
  } catch (e:any) { error.value = e?.message || 'No se pudo transferir la propiedad.'; }
  finally { busy.value = false; }
}

async function copyLink() {
  try { await navigator.clipboard.writeText(invitationLink.value); message.value = 'Enlace copiado.'; }
  catch { message.value = 'No se pudo copiar automáticamente; utiliza la copia manual del enlace.'; }
}

onMounted(() => { currentUser(); if (authenticated.value) void loadAll(); });
</script>

<template>
  <main class="phase38">
    <header class="hero">
      <div>
        <p class="eyebrow">FASE 38 · 473–480</p>
        <h1>Compartición de casos</h1>
        <p>Comparte un caso con otros usuarios, asigna lectura o edición, revoca accesos y transfiere la propiedad. Todo se valida en NestJS antes de tocar MariaDB.</p>
      </div>
      <button class="ghost" type="button" @click="router.push('/')">← Volver al tablero</button>
    </header>

    <section v-if="!authenticated" class="notice">
      <h2>Necesitas una cuenta</h2>
      <p>La compartición utiliza la cuenta autenticada y los permisos almacenados en el servidor.</p>
      <button type="button" @click="router.push('/login')">Iniciar sesión</button>
    </section>

    <template v-else>
      <section v-if="incoming.length" class="panel incoming">
        <div class="panel-head"><div><h2>Invitaciones recibidas</h2><small>Las invitaciones solo se aceptan para el email de la cuenta autenticada.</small></div><span class="badge">{{ incoming.length }}</span></div>
        <div v-for="inv in incoming" :key="inv.id" class="row invite-row">
          <div><strong>{{ inv.email }}</strong><small>Permiso: {{ permissionLabel(inv.permission) }} · {{ formatTime(inv.createdAt) }}</small></div>
          <button type="button" :disabled="busy" @click="accept(inv)">Aceptar</button>
        </div>
      </section>

      <section class="layout">
        <aside class="panel case-list">
          <div class="panel-head"><div><h2>Tus casos</h2><small>Incluye casos propios y compartidos.</small></div><span class="badge">{{ cases.length }}</span></div>
          <button v-for="c in cases" :key="c.id" type="button" :class="['case-item',{active:c.id===selectedCaseId}]" @click="loadCase(c.id)">
            <strong>{{ caseLabel(c) }}</strong><small>{{ permissionLabel(c.permission) }} · v{{ c.version }}</small>
          </button>
          <p v-if="!cases.length" class="empty">Todavía no tienes casos sincronizados con el servidor.</p>
        </aside>

        <section v-if="selectedCase" class="stack">
          <section class="panel">
            <div class="panel-head"><div><h2>Compartir “{{ caseLabel(selectedCase) }}”</h2><small>{{ isOwner ? 'Eres el propietario y administras el acceso.' : `Tienes permiso de ${permissionLabel(selectedCase.permission)}.` }}</small></div><span class="permission" :class="selectedCase.permission">{{ permissionLabel(selectedCase.permission) }}</span></div>
            <div v-if="isOwner" class="share-form">
              <input v-model.trim="email" type="email" autocomplete="off" placeholder="persona@ejemplo.com" :disabled="busy" @keyup.enter="share">
              <select v-model="permission" :disabled="busy"><option value="read">Solo lectura</option><option value="edit">Puede editar</option></select>
              <button type="button" :disabled="busy || !email" @click="share">Compartir</button>
            </div>
            <p v-else class="muted">Solo el propietario puede añadir, quitar o cambiar colaboradores.</p>
            <div v-if="invitationLink" class="invite-link"><div><strong>Enlace de invitación</strong><small>El destinatario puede abrirlo para entrar o crear su cuenta con ese email.</small></div><code>{{ invitationLink }}</code><button type="button" @click="copyLink">Copiar</button></div>
          </section>

          <section class="panel">
            <div class="panel-head"><div><h2>Colaboradores</h2><small>Los permisos se aplican en backend; la interfaz no puede saltárselos.</small></div></div>
            <div v-for="m in members" :key="m.userId" class="row member-row">
              <div class="avatar">{{ (m.name || m.email || '?').slice(0,1).toUpperCase() }}</div>
              <div class="member-main"><strong>{{ m.name || m.email }}</strong><small>{{ m.email }}</small></div>
              <span class="permission" :class="m.permission">{{ permissionLabel(m.permission) }}</span>
              <template v-if="isOwner && m.permission !== 'owner'">
                <button type="button" :disabled="busy" @click="changePermission(m)">{{ m.permission === 'edit' ? 'Pasar a lectura' : 'Dar edición' }}</button>
                <button type="button" class="danger" :disabled="busy" @click="remove(m)">Revocar</button>
              </template>
            </div>
            <p v-if="!members.length" class="empty">Este caso todavía no tiene colaboradores.</p>
          </section>

          <section v-if="isOwner" class="panel">
            <div class="panel-head"><div><h2>Invitaciones pendientes</h2><small>Puedes cancelar un enlace antes de que el destinatario lo acepte.</small></div></div>
            <div v-for="inv in outgoing" :key="inv.id" class="row invite-row">
              <div><strong>{{ inv.email }}</strong><small>{{ permissionLabel(inv.permission) }} · {{ formatTime(inv.createdAt) }}</small></div>
              <button type="button" class="danger" :disabled="busy" @click="revokeInvitation(inv)">Cancelar</button>
            </div>
            <p v-if="!outgoing.length" class="empty">No hay invitaciones pendientes.</p>
          </section>

          <section v-if="isOwner" class="panel transfer">
            <div class="panel-head"><div><h2>Transferir propiedad</h2><small>El nuevo propietario tendrá control total. Tú conservarás acceso de edición.</small></div></div>
            <button v-if="!showTransfer" type="button" @click="showTransfer = true">Transferir propiedad…</button>
            <div v-else class="transfer-form">
              <select v-model="transferTarget" :disabled="busy"><option disabled value="">Selecciona un colaborador</option><option v-for="m in transferCandidates" :key="m.userId" :value="m.userId">{{ m.name || m.email }} · {{ m.email }}</option></select>
              <button type="button" :disabled="busy || !transferTarget" @click="transferOwnership">Confirmar transferencia</button>
              <button type="button" class="ghost" :disabled="busy" @click="showTransfer = false">Cancelar</button>
            </div>
            <p v-if="!transferCandidates.length" class="muted">Primero comparte el caso con un usuario para poder transferir la propiedad.</p>
          </section>
        </section>
      </section>
    </template>

    <p v-if="error" class="feedback error" role="alert">{{ error }}</p>
    <p v-if="message" class="feedback success" role="status">{{ message }}</p>

    <section class="requirements">
      <article><b>473–474</b><h3>Compartir e invitar</h3><p>Usuarios existentes reciben acceso directo; los demás reciben una invitación pendiente con enlace seguro.</p></article>
      <article><b>475–476</b><h3>Lectura y edición</h3><p>Cada colaborador tiene un permiso único y el backend lo comprueba antes de leer o escribir.</p></article>
      <article><b>477</b><h3>Revocar acceso</h3><p>El propietario puede quitar colaboradores y cancelar invitaciones pendientes.</p></article>
      <article><b>478–480</b><h3>Propiedad y permisos</h3><p>La propiedad puede transferirse a un colaborador; el propietario mantiene el control sobre la gestión de permisos.</p></article>
    </section>
  </main>
</template>

<style scoped>
.phase38{min-height:100dvh;box-sizing:border-box;padding:24px 24px 80px;background:#09111e;color:#e9eef6;font:14px/1.45 system-ui,sans-serif}.hero,.panel-head{display:flex;justify-content:space-between;gap:16px;align-items:flex-start}.hero h1{font-size:clamp(30px,6vw,52px);margin:3px 0 8px}.eyebrow{font-size:11px;font-weight:800;letter-spacing:.16em;color:#92a6bf}.hero>div>p:last-child,.muted,.empty,small{color:#97a8bb}.ghost,.phase38 button{border:1px solid #33475f;background:#17243a;color:#edf4fb;border-radius:10px;padding:10px 13px;font:inherit;cursor:pointer}.phase38 button:disabled{opacity:.5;cursor:not-allowed}.ghost{background:transparent}.notice,.panel,.requirements article{margin-top:18px;padding:18px;border:1px solid #26374e;background:#111c2d;border-radius:16px}.notice h2,.panel h2,.requirements h3{margin:0 0 4px}.layout{display:grid;grid-template-columns:280px minmax(0,1fr);gap:16px;margin-top:18px}.case-list{align-self:start}.case-list .panel-head{align-items:center;margin-bottom:10px}.badge{display:inline-grid;place-items:center;min-width:28px;height:28px;padding:0 8px;box-sizing:border-box;border:1px solid #30435c;border-radius:999px;background:#152237;color:#bfd0e3}.case-item{display:grid;width:100%;text-align:left;gap:3px;margin-top:8px}.case-item small{font-size:12px}.case-item.active{outline:2px solid #7595ba}.stack{display:grid;gap:16px;min-width:0}.share-form,.transfer-form{display:grid;grid-template-columns:minmax(0,1fr) auto auto;gap:8px;margin-top:14px}.share-form input,.share-form select,.transfer-form select{min-width:0;background:#0d1726;border:1px solid #30425a;color:#fff;border-radius:10px;padding:10px 12px;font:inherit}.permission{display:inline-flex;align-items:center;padding:7px 10px;border-radius:999px;border:1px solid #2e4058;font-weight:700}.permission.owner{background:#1c3047}.permission.edit{background:#1d3930}.permission.read{background:#332e1e}.row{display:grid;grid-template-columns:auto minmax(0,1fr) auto auto auto;gap:10px;align-items:center;border-top:1px solid #23344a;padding:11px 0}.invite-row{grid-template-columns:1fr auto}.member-main{display:grid;min-width:0}.avatar{width:34px;height:34px;display:grid;place-items:center;border-radius:50%;background:#22334a;font-weight:800}.member-row:first-of-type,.invite-row:first-of-type{border-top:0}.danger{border-color:#5b3943!important}.invite-link{display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:10px;align-items:center;margin-top:14px;padding:12px;border:1px solid #30516c;background:#102235;border-radius:12px}.invite-link div{display:grid}.invite-link code{min-width:0;overflow:auto;white-space:nowrap;color:#c5d7ea}.feedback{margin-top:14px;padding:11px 13px;border-radius:10px}.error{background:#351e26;border:1px solid #69404b;color:#ffc0c0}.success{background:#173328;border:1px solid #32614e;color:#a5e8c6}.requirements{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin-top:18px}.requirements article{margin-top:0}.requirements p{margin:0;color:#97a8bb}.transfer{background:#121d2c}.transfer-form{grid-template-columns:minmax(0,1fr) auto auto}.transfer>p{margin-bottom:0}@media(max-width:900px){.layout{grid-template-columns:1fr}.requirements{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:640px){.phase38{padding:15px 12px 70px}.hero{flex-direction:column}.share-form,.transfer-form{grid-template-columns:1fr}.row.member-row{grid-template-columns:auto 1fr auto}.member-row>button{grid-column:2}.member-row>.danger{grid-column:2}.invite-link{grid-template-columns:1fr}.requirements{grid-template-columns:1fr}.panel-head{flex-direction:column}.panel-head .permission{align-self:flex-start}}
</style>
